import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Eye } from 'lucide-react';
import { Link } from 'react-router';
import { cardReportingTimeOf, readableDate, toTwelveHour, wholeDayTimes } from '@pams/shared';
import type { ArrangementDto, DutyPointDto, SharedColumnDto } from '@pams/shared';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/StatusPill';
import { RaiserPill } from '@/components/RaiserPill';
import { Loading, Skeleton } from '@/components/Skeleton';
import { api } from '@/lib/api';
import { namedOf, orderStatusWord, presenceLine } from '@/lib/statusPresentation';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/utils';
import { PREVIEW_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1e §2] THE QUICK PREVIEW — VIEW-ONLY, AND IT RENDERS THE DASHBOARD'S OWN DTO.
 *
 *  ⚠️ IT IS NOT A THIRD DETAIL RENDERER OF A THIRD SHAPE. `GET /arrangements/:id?view=preview`
 *  returns the SAME `ArrangementDto` the detail page already reads, with three keys filled in.
 *  Same object, same status vocabulary, different density — so a number here and a number on
 *  `/arrangements/:id` cannot disagree.
 *
 *  ⚠️ THE BRIEF ASKED FOR [P6d]'s OPERATIONS DTO AND THAT IS UNREACHABLE FROM HERE. Every
 *  `/app/*` route is `OFFICER_ONLY`; a `pams:dashboard` token gets `403 WRONG_SURFACE`, which
 *  `officerSurface.test.ts` pins. `grep -rn "'/app/" web/src` returns nothing, and it must go
 *  on returning nothing.
 *
 *  ⚠️ A shadcn `Dialog`, NOT AN ANTD `Modal`, AND THE DECIDING REASON IS THIS APP'S SHELL.
 *  `AppShell` is `h-screen overflow-hidden` with `<main>` as the one scroller — the DOCUMENT
 *  never scrolls. AntD's scroll lock sets `overflow: hidden` on `document.body`, which locks
 *  nothing here; Radix wraps its content in `react-remove-scroll`, which also captures wheel
 *  and touch outside the content. §2g's "the body behind does not scroll" is therefore free on
 *  one primitive and absent on the other. (`GlobalSearch.tsx` already ships this exact
 *  fixed-header-over-a-scrolling-body pattern on the same primitive.)
 *
 *  ⚠️ `vw`/`vh` ARE CORRECT HERE AND NOWHERE ELSE IN `web/`. §8 requires CONTAINER queries
 *  because `<main>` is 304px narrower than the window — but a dialog is portalled to
 *  `document.body`, outside `<main>`, and is anchored to the viewport. The viewport is what it
 *  must measure. These are the first such units in `web/src`.
 *
 *  ⚠️ AND `ui/dialog.tsx` IS NOT EDITED. Three other consumers depend on its `max-w-lg` and
 *  its `p-6`; this passes its own classes, which win because `cn` is `twMerge`-backed.
 *  `grid-rows-[auto_minmax(0,1fr)]` is what makes the header fixed and the body the only
 *  scroller — a `minmax(0,1fr)` row may shrink below its content, where a bare `1fr` may not.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** ⚠️ [P6a] `tabular-nums`, so a refetch cannot reflow the strip under the reader. */
const Figure = ({ label, value }: { label: string; value: number | null | undefined }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-2xl font-semibold tabular-nums text-foreground">
      {value === null || value === undefined ? PREVIEW_COPY.none : value.toLocaleString('en-IN')}
    </span>
  </div>
);

import { ShortfallNotes } from './ShortfallNotes';
import { CommandContacts } from './CommandContacts';
import { awaitedIsLive } from '@/lib/commandWords';
import { ForceMatrix } from './ForceMatrix';
import { SharedColumnLines } from './SharedColumnLines';
import { coveredElsewhere } from '@/lib/sharedColumns';

/**
 * ⚠️ [C1h §4e] INSTRUCTIONS RENDER WHEN PRESENT, AND THEY WERE ON THE WIRE ALL ALONG.
 *
 * [C1c] put them on the ARRANGEMENT and on each DUTY POINT, and **neither appeared on this
 * screen at all** — a grep for `instructions` in this file returned nothing before [C1h]. A
 * field that is collected, stored, sent and never shown is [P6b.1]'s defect exactly: "a screen
 * that renders nothing is indistinguishable from a screen with nothing to render".
 */
const Instructions = ({ lines, testId }: { lines: readonly { text: string }[]; testId: string }) =>
  lines.length === 0 ? null : (
    <ul className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-muted-foreground" data-testid={testId}>
      {lines.map((line, index) => (
        <li key={`${String(index)}-${line.text}`}>{line.text}</li>
      ))}
    </ul>
  );

const DutyPointCard = ({
  point,
  open,
  shared,
  ordinalOf,
  live,
}: {
  point: DutyPointDto;
  open: boolean;
  /** [C5c §2] The order's shared columns — `[]` on every order that shares nothing. */
  shared: readonly SharedColumnDto[];
  ordinalOf: (dutyPointId: string) => number | undefined;
  /** [C6a] `awaitedIsLive(record)` — whether an empty command position may still read "awaited". */
  live: boolean;
}) => {
  const duty = point.preview?.duty ?? null;
  const counts = point.preview;
  /*
   * ⚠️ [C5b §3e] THE CARD'S OWN REPORTING TIME, THROUGH THE ONE SHARED RULE — a time on a whole-day
   * card and never on a shift card, whose duties each report at their own start. The server already
   * answers `null` for a shift card; calling the same function here is not a second expression of the
   * rule, and it keeps this line right against a record served before that pass.
   */
  const reportingTime = cardReportingTimeOf({
    timingKind: point.schedule.timing.kind,
    reportingTime: point.reportingTime,
  });

  return (
    <details
      open={open}
      data-testid={`preview-point-${point.id}`}
      className="rounded-lg border border-border bg-card px-3 py-2"
    >
      {/*
        ⚠️ [C5c §4] A COLUMN OF TWO LINES NOW, AND STILL `flex` — the display that keeps the native
        disclosure marker away in every engine. The first line is the row it always was (the title
        truncating beside its counts); the second is the SECTOR, which has no length limit and so
        cannot share a truncating line: it wraps, whole, on a line of its own.
      */}
      <summary className="flex cursor-pointer list-none flex-col gap-0.5">
        <span className="flex items-baseline justify-between gap-3">
          <span className="min-w-0 truncate text-sm font-medium text-foreground" title={point.displayTitle}>
            DP{point.displayOrder + 1} · {point.displayTitle}
            {/* [C4a §4e] The zone when there is one, and nothing at all when there is not — or when the key is absent. */}
            {point.zone ? (
              <span className="font-normal text-muted-foreground" data-testid={`preview-zone-${point.id}`}>
                {' · '}
                {PREVIEW_COPY.zone(point.zone)}
              </span>
            ) : null}
          </span>
          <span className="flex shrink-0 items-baseline gap-3 text-xs text-muted-foreground">
            {duty ? (
              <span>
                {readableDate(duty.dutyDate)} · {duty.timeLabel}
              </span>
            ) : null}
            {/* ⚠️ [C5b §3] `namedOf`, THE ONE SPELLING — this card read `12 of 12` beside surfaces
                reading `12 of 12 named` (D10). `PREVIEW_COPY.namedOf` stays for the matrix's CELLS,
                where the column heading already says what is counted. */}
            <span className="font-medium tabular-nums text-foreground">
              {namedOf(counts?.namedCount ?? 0, counts?.allocatedCount ?? 0)}
            </span>
          </span>
        </span>
        {/* [C5c §4] A truthiness test, as the zone's: `null`, `''` and a missing key all render nothing. */}
        {point.sector ? (
          <span
            className="min-w-0 break-words text-xs text-muted-foreground"
            data-testid={`preview-sector-${point.id}`}
          >
            {PREVIEW_COPY.sector(point.sector)}
          </span>
        ) : null}
      </summary>

      <div className="mt-2 flex flex-col gap-2 border-t border-border pt-2">
        {/*
          ⚠️ [C1c §1] REPORTING TIME AND PLACE ARE INDEPENDENT — GUARD ON THE PAIR. Two
          renderers shipped `reportingPlace === null ? null : …` and showed nothing at all for
          a card carrying only a time. This is the third opportunity to get it wrong.
        */}
        {reportingTime !== null || point.reportingPlace !== null || point.standDownTime !== null ? (
          <p className="text-xs text-muted-foreground" data-testid={`preview-reporting-${point.id}`}>
            {/*
              ⚠️ [C3a §6] `toTwelveHour`, LIKE EVERY OTHER READER OF THIS FIELD.
              `arrangement.schema.ts` says of `reportingTime`: "`HH:mm`, or `null`. Rendered
              12-hour; never stored that way." This was the ONE unconverted render in the
              repository — the print sheet's structural twin (the same two fields through the
              same `.filter(Boolean).join(' · ')`) converts, so one order showed `15:00` in the
              preview and `3:00 PM` on the paper it previews.
            */}
            {[
              /* [STAND-DOWN] With a stand-down time the card states its window (`3:00 PM–9:00 PM`);
                 without one it keeps the bare reporting time it always showed. */
              point.standDownTime === null
                ? reportingTime === null
                  ? null
                  : toTwelveHour(reportingTime)
                : wholeDayTimes(reportingTime, point.standDownTime),
              point.reportingPlace?.name,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}

        {point.commandEntries.length > 0 ? (
          /*
            ⚠️ [amended C1f §2b] NAMES **AND** PHONE NUMBERS, INLINE AND UNCOUNTED.

            The force half of this card carries no names at all (§2a), so a phone cannot leak
            through it; a supervisory seat is the opposite case and always has been. [P5a]
            settled it structurally — a force officer's number is absent from the payload and
            a commander's is inline — and [C1b §5c] confirmed no reveal step and no meter for
            the second. This DISCLOSES NOTHING NEW: `?view=record` already returns these exact
            bytes to every `arrangement.read` holder in scope — and the printed order carries
            them too since [C5 §7b].

            ⚠️ NAMED, NEVER COUNTED — FR-5.2.6b. No number may appear beside one of these
            rows, which is why the roster above and this block are rendered separately rather
            than sharing a row component.
          */
          <CommandContacts point={point} live={live} />
        ) : null}

        {/* [C5c §2] A column asked for once across the order — see `SharedColumnLines`. */}
        <SharedColumnLines shared={shared} dutyPointId={point.id} ordinalOf={ordinalOf} testIdPrefix="preview-shared" />

        {/* ⚠️ [P6b.2] A CARD WITH NOTHING ANSWERS FOR ITSELF rather than rendering empty. */}
        {duty === null ? (
          <p className="text-sm text-muted-foreground">{PREVIEW_COPY.noDuty}</p>
        ) : duty.posts.length === 0 ? (
          /* ⚠️ [C5c §2] Not on a covered card — its force is the shared column, stood elsewhere. */
          coveredElsewhere(shared, point.id) ? null : (
            <p className="text-sm text-muted-foreground">{PREVIEW_COPY.noForce}</p>
          )
        ) : (
          <ForceMatrix duty={duty} />
        )}

        {/*
          ⚠️ [C4a §7a] NOTES AFTER THE WHOLE TABLE — every division or unit that closed this duty short,
          with the reason it gave. The preview carries ONE resolved duty per card, so these are that
          duty's, the one the matrix above describes. No heading when there is nothing to say (§7d).
        */}
        {/*
          ⚠️ [amended C5b §3] GROUPED UNDER `Still to come` AND `Closed short`, the printed order's
          own two headings — the preview headed both `Notes` (D11), so a division that can still be
          chased read the same as one that has already replied.
        */}
        {/* ⚠️ [P7A §4] `ShortfallNotes` — the same markup, now shared with the PA record page. */}
        {duty === null ? null : <ShortfallNotes notes={duty.shortfalls} testId={`preview-shortfalls-${point.id}`} />}

        {/*
          ⚠️ [C5c §5a] THE OPENED DUTY'S OWN ATTENDANCE, UNDER THE MATRIX IT DESCRIBES — the notes'
          rule above. `null` until that duty has started, so there is then nothing to say.
        */}
        {duty?.presence ? (
          <p className="text-xs text-muted-foreground" data-testid={`preview-point-attendance-${point.id}`}>
            {PREVIEW_COPY.attendance}: {presenceLine(duty.presence)}
          </p>
        ) : null}

        {/* ⚠️ [C1h §4e] THE CARD'S OWN standing orders, under the force it applies to. */}
        <Instructions lines={point.instructions} testId={`preview-instructions-${point.id}`} />
      </div>
    </details>
  );
};

export const ArrangementPreviewModal = ({
  arrangementId,
  onClose,
  openedFrom,
}: {
  arrangementId: string | null;
  onClose: () => void;
  /**
   * ⚠️ [C1e §2g] WHICH EYE OPENED IT — a ROW key, not the arrangement id, because several rows
   * of one list can name the same order. `undefined` simply means no restoration is attempted.
   */
  openedFrom?: string | null;
}) => {
  /**
   * ─────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C1e §2g] WHICH EYE FOCUS GOES BACK TO — REMEMBERED HERE, AND NOT A PROP.
   *
   *  Radix restores focus to whatever was focused before the dialog mounted, and that is right
   *  until the node it captured is REPLACED while the dialog is open — a tile refetch
   *  re-renders its rows and `TileCard` swaps the body for a skeleton while it does. A detached
   *  node cannot take focus, so it lands on `<body>` and a keyboard user is returned to the top
   *  of the page.
   *
   *  ⚠️ TWO EARLIER ATTEMPTS WERE MEASURABLY WRONG, AND BOTH FAILED THE SAME WAY. Holding the
   *  ELEMENT goes stale by exactly the mechanism Radix's own capture does. Taking the id as a
   *  PROP is worse and subtler: Escape calls `onOpenChange(false)` → the host clears its state →
   *  this component re-renders with `null` → and only THEN does `onCloseAutoFocus` fire, so the
   *  prop is already gone at the one moment it is needed.
   *
   *  So the id is remembered in a ref written from an EFFECT (never during render —
   *  `react-hooks/refs`), survives the close, and the eye is re-queried from the DOM as it is
   *  rather than as it was.
   * ─────────────────────────────────────────────────────────────────────────────────
   */
  const openedFor = useRef<string | null>(null);
  useEffect(() => {
    if (openedFrom !== undefined && openedFrom !== null) openedFor.current = openedFrom;
  }, [openedFrom]);

  const preview = useQuery({
    queryKey: queryKeys.arrangements.preview(arrangementId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ArrangementDto>(`/arrangements/${arrangementId ?? ''}`, { query: { view: 'preview' }, signal }),
    enabled: arrangementId !== null,
  });

  const record = preview.data;

  /*
   * ⚠️ WHICH CARD IS OPEN IS THE CLIENT'S ONLY JOB HERE. The SERVER already resolved WHICH
   * DUTY each card carries and echoed it, so there is no second expression of "nearest
   * upcoming" to disagree with it at midnight.
   */
  const openIndex = record ? record.dutyPoints.findIndex((point) => point.preview?.duty !== null) : -1;

  /* ⚠️ [D4] THE POLICY IS ON THE DUTY POINT, so an order has 0..N of them. */
  const policies = record ? new Set(record.dutyPoints.map((point) => point.policyId)) : new Set<string>();
  const policyLabel =
    record && policies.size === 1
      ? (record.dutyPoints[0]?.policy?.name ?? null)
      : policies.size > 1
        ? PREVIEW_COPY.policies(policies.size)
        : null;

  return (
    <Dialog open={arrangementId !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        onCloseAutoFocus={(event) => {
          /* ⚠️ Re-queried NOW — see `openedFor`. A node captured at open time may be gone. */
          const id = openedFor.current;
          if (id === null) return;
          const eye = document.querySelector<HTMLElement>(`[data-preview-for="${id}"]`);
          if (!eye) return;

          /*
           * ⚠️ DEFERRED A FRAME, AND THAT IS THE DIFFERENCE BETWEEN jsdom AND A BROWSER.
           *
           * MEASURED: focusing synchronously here works in jsdom and lands on `<body>` in
           * Chromium. While the dialog is open Radix marks everything outside it as hidden from
           * the rest of the page, and that treatment is still in force at the moment
           * `onCloseAutoFocus` fires — jsdom does not implement it, so the synchronous version
           * looked correct in the unit suite for three browser runs.
           *
           * `preventDefault` stops Radix restoring to the node IT captured (which may be stale),
           * and the frame lets the teardown finish before the eye is asked to take focus.
           */
          event.preventDefault();
          requestAnimationFrame(() => {
            eye.focus();
          });
        }}
        data-testid="arrangement-preview"
        className={cn('max-h-[80vh] w-[80vw] max-w-[80vw] grid-rows-[auto_minmax(0,1fr)] gap-0 p-0', 'sm:max-w-[80vw]')}
      >
        {/* ⚠️ THE FIXED HEADER. It is a grid ROW, not a `sticky` — see the file header. */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">
                {record?.paNumber ?? record?.reference ?? ''}
              </span>
              {/* ⚠️ [C5b §3] `orderStatusWord` — this pill said `In the Archive` and the record page
                  `Archived` for one fact (D9). */}
              {record ? (
                <StatusPill tone={orderStatusWord(record).tone}>{orderStatusWord(record).label}</StatusPill>
              ) : null}
              {/* [P7A §2] Who raised it, after the state — the record page's order; nothing when `null`. */}
              {record ? <RaiserPill level={record.raisedAtLevel} /> : null}
            </div>
            {/*
              ⚠️ NEVER AN EMPTY STRING. `DialogTitle` IS the dialog's accessible name, so while the
              fetch is in flight a screen reader announced an unnamed dialog — which is the one
              moment a blind user most needs to be told what opened.
            */}
            <DialogTitle className="truncate text-base">{record?.displayTitle ?? PREVIEW_COPY.loading}</DialogTitle>
            {record ? (
              <p className="text-xs text-muted-foreground">
                {[
                  record.firstDutyDate ? readableDate(record.firstDutyDate) : readableDate(record.paDate),
                  PREVIEW_COPY.dutyPoints(record.dutyPointCount),
                  policyLabel,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            ) : null}
          </div>

          {/*
            ⚠️ `Open full` LIVES IN THE HEADER. §2a: the point of a preview is deciding whether
            to leave it, and making somebody close the modal first is the friction that stops
            people using previews at all.
          */}
          {arrangementId ? (
            /*
              ⚠️ `mr-8` ON THE WRAPPER, SO THE DIALOG'S OWN CLOSE BUTTON SITS ON TOP OF NEITHER.
              `ui/dialog.tsx` paints an absolutely-positioned X at `right-4 top-4`; without the
              margin its hit area overlapped the rightmost button's top-right corner, so part of
              "Open full" closed the preview instead of navigating. [C5c §6a] moved the margin from
              that button to this wrapper when a second control joined it, so the X keeps clear of
              whichever button ends up at the edge — including after the row wraps.
            */
            <div className="mr-8 flex shrink-0 flex-wrap items-center gap-2">
              {/*
                ⚠️ [C5c §6a/§6b] PRINT OPENS THE EXISTING SHEET — THE SAME ROUTE THE RECORD PAGE LINKS
                TO, NEVER A PRINT OF THIS MODAL. The sheet carries the per-source blocks, the phone
                numbers and the attendance line; a modal-shaped print would be a second document that
                drifts from the first within a pass. `printDocument.test.ts` holds the census.

                A REAL `<a>` in a new tab — `NameCell`'s rule and the record page's own link: ⌘-click
                and copy-link work, and the preview stays open behind it. No Tooltip, so the overlay
                census does not move; the route is `arrangement.read` + `requireArrangement` and is
                audited as `ORDER_SHEET_VIEWED`, so this opens nothing the viewer could not already reach
                through `Open full`.
              */}
              <Button asChild variant="outline" size="sm" data-testid="preview-print">
                <a href={`/arrangements/${arrangementId}/print`} target="_blank" rel="noreferrer">
                  {PREVIEW_COPY.print}
                </a>
              </Button>
              <Button asChild variant="outline" size="sm" data-testid="preview-open-full">
                <Link to={`/arrangements/${arrangementId}`}>{PREVIEW_COPY.openFull}</Link>
              </Button>
            </div>
          ) : null}
        </div>

        {/* ⚠️ THE ONLY SCROLLER. `minmax(0,1fr)` above is what lets it shrink and scroll. */}
        <div data-testid="preview-body" className="min-h-0 overflow-y-auto px-5 py-4">
          {preview.isError ? (
            /*
             * ⚠️ AN ERROR BRANCH, BECAUSE THE ALTERNATIVE IS AN ETERNAL SKELETON. Without it a
             * failed fetch left `record` undefined for ever, so the loading state rendered
             * permanently — and `Loading` is a `role="status"` LIVE REGION, so a screen reader
             * was told "Loading the order" with nothing ever arriving.
             */
            <p className="text-sm text-muted-foreground">{PREVIEW_COPY.failed}</p>
          ) : !record ? (
            <Loading label={PREVIEW_COPY.loading}>
              <Skeleton className="h-40 w-full" />
            </Loading>
          ) : (
            <div className="flex flex-col gap-4">
              {/* ⚠️ §2b — THE NUMBERS BEFORE ANY DETAIL. "Is this order in trouble?" */}
              <div className="grid grid-cols-2 gap-4 @min-[560px]:grid-cols-3" data-testid="preview-figures">
                <Figure label={PREVIEW_COPY.required} value={record.required} />
                {/* ⚠️ `sourced` IS `allocatedCount` and `named` IS `namedCount` — the keys the
                    detail page already reads, so the two screens cannot disagree. */}
                <Figure label={PREVIEW_COPY.sourced} value={record.allocatedCount} />
                <Figure label={PREVIEW_COPY.named} value={record.namedCount} />
              </div>

              {/*
                ⚠️ [C5c §5a] THE ORDER'S ATTENDANCE, IN WORDS — AND NOTHING AT ALL BEFORE A DUTY HAS
                STARTED. It REPLACED the `Reached` figure, which counted Reached alone and rendered
                `—` until any marking existed. `presence` is `null` until a duty of the order starts,
                so an order issued days early shows no line rather than a row of zeros (§6d).
              */}
              {record.presence ? (
                <p className="text-sm text-foreground" data-testid="preview-attendance">
                  {PREVIEW_COPY.attendance}: {presenceLine(record.presence)}
                </p>
              ) : null}

              <div className="flex flex-col gap-2">
                {record.dutyPoints.map((point, index) => (
                  <DutyPointCard
                    key={point.id}
                    point={point}
                    open={index === openIndex}
                    /* ⚠️ `?? []` — a cached response from before the key existed. */
                    shared={record.sharedColumns ?? []}
                    live={awaitedIsLive(record)}
                    ordinalOf={(id) => {
                      const found = record.dutyPoints.find((row) => row.id === id);
                      return found ? found.displayOrder + 1 : undefined;
                    }}
                  />
                ))}
              </div>

              {/*
                ⚠️ [C1h §4e] THE ORDER'S OWN INSTRUCTIONS, BENEATH EVERY CARD.

                They apply to the whole arrangement rather than to one duty point, so they sit
                after all of them — the position the printed sheet gives them. A card's own
                standing orders render inside that card, under the force they apply to.
              */}
              <Instructions lines={record.instructions} testId="preview-instructions" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * The eye that opens it.
 *
 * ⚠️ A `<button>`, NEVER A LINK — `NameCell`'s rule: anything that opens a panel in place is a
 * button, and anything that NAVIGATES is a real `<a>`. It carries the order's name in its
 * accessible name, so a list of them is not a list of controls all called "Preview".
 *
 * ⚠️ `relative z-10`, so it survives a tile whose whole card is a stretched drill overlay.
 */
export const PreviewEye = ({
  eyeKey,
  title,
  onOpen,
}: {
  /**
   * ⚠️ [C1e §2g] UNIQUE PER **ROW**, NEVER THE ARRANGEMENT ID.
   *
   * Both hosts render several rows per order — the dashboard tile is one row per DUTY, and the
   * inbox one per duty-point-and-division — so keying this on the arrangement made
   * `document.querySelector` return the FIRST match and closing a preview opened from the
   * second or third row put focus on a different row's eye. Silent, and wrong for exactly the
   * users who cannot see the list.
   */
  eyeKey: string;
  title: string;
  onOpen: () => void;
}) => (
  <button
    type="button"
    data-testid="preview-eye"
    /* ⚠️ [C1e §2g] THE HANDLE FOCUS COMES BACK TO — unique per ROW, see `eyeKey`. */
    data-preview-for={eyeKey}
    aria-label={`Preview ${title}`}
    onClick={(event) => {
      event.stopPropagation();
      onOpen();
    }}
    className="relative z-10 inline-flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-active hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    <Eye className="size-4" />
  </button>
);
